import ProgramsHierarchy from '@/shared/components/ProgramsHierarchy';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { ClassicEditor, Bold, Essentials, Italic, Mention, Paragraph, Undo, Underline, Link, Code, Strikethrough, Subscript, Superscript, Heading, List, Alignment, SourceEditing, SpecialCharacters, SpecialCharactersEssentials, Image, ImageInsert, Indent, IndentBlock, Table, TableToolbar, MediaEmbed } from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import emojis from '@/shared/components/emoji/emojis';
import { connect, useDispatch } from 'react-redux';
import axios from 'axios';
import { flashError, flashSuccess } from '@/shared/components/flash';

const PushNotifications = ({ program }) => {
  const { t } = useTranslation();
  const translatedTitle = t("mobile_notificaion_message");
  const pageTitle = translatedTitle && translatedTitle !== "mobile_notificaion_message"
    ? translatedTitle
    : "Push Notifications";
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [value, setValue] = useState("");
  const [plainTextValue, setPlainTextValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const dispatch = useDispatch();

  function SpecialCharactersEmoji(editor) {
    editor.plugins.get('SpecialCharacters').addItems('Emoji', emojis, { label: 'Emoticons' });
  }

  const selectedProgramCount = selectedPrograms.length;
  const hasMessage = plainTextValue.trim().length > 0;
  const isSubmitDisabled = !hasMessage || selectedProgramCount === 0 || isSubmitting;

  const handleProgramsChange = (programIds) => {
    setSelectedPrograms(Array.isArray(programIds) ? [...programIds] : []);
  };

  const onSubmit = async () => {
    if (isSubmitDisabled) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`/organization/${program.organization_id}/program/${program.id}/send-push-notification`, {
        title: pageTitle,
        body: value,
        programs: selectedPrograms,
      });

      if (response.status === 200) {
        flashSuccess(dispatch, "Push notification sent successfully!");
        setSelectedPrograms([]);
        setValue("");
        setPlainTextValue("");
        setEditorKey((previous) => previous + 1);
      }
    } catch (error) {
      if (error) {
        flashError(dispatch, error?.response?.data || "Unable to send the push notification.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="manager-mobile-app">
      <section className="manager-card manager-mobile-app__hero">
        <div className="manager-mobile-app__hero-copy">
          <span className="manager-card__eyebrow">Mobile App</span>
          <h1 className="manager-mobile-app__title">{pageTitle}</h1>
          <p className="manager-mobile-app__description">
            Choose the programs that should receive a notification, compose the message, and send it from the same manager workspace.
          </p>
        </div>
        <div className="manager-mobile-app__hero-stats">
          <div className="manager-mobile-app__hero-stat">
            <span className="manager-mobile-app__hero-stat-label">Programs</span>
            <strong className="manager-mobile-app__hero-stat-value">{selectedProgramCount.toLocaleString()}</strong>
          </div>
          <div className="manager-mobile-app__hero-stat">
            <span className="manager-mobile-app__hero-stat-label">Message</span>
            <strong className="manager-mobile-app__hero-stat-value">{hasMessage ? "Ready" : "Draft"}</strong>
          </div>
        </div>
      </section>

      <div className="manager-mobile-app__layout">
        <article className="manager-card manager-mobile-app__panel manager-mobile-app__panel--audience">
          <div className="manager-mobile-app__panel-head">
            <div>
              <span className="manager-card__eyebrow">Audience</span>
              <h3 className="manager-card__title">Target Programs</h3>
            </div>
            <span className="manager-mobile-app__badge">{selectedProgramCount} selected</span>
          </div>
          <p className="manager-mobile-app__panel-description">
            Select the programs that should receive this message.
          </p>
          <div className="manager-mobile-app__programs">
            <ProgramsHierarchy
              label="Send to Programs"
              selectedPrograms={selectedPrograms}
              setSelectedPrograms={handleProgramsChange}
            />
          </div>
        </article>

        <article className="manager-card manager-mobile-app__panel manager-mobile-app__panel--composer">
          <div className="manager-mobile-app__panel-head">
            <div>
              <span className="manager-card__eyebrow">Message</span>
              <h3 className="manager-card__title">Compose Notification</h3>
            </div>
            <span className={`manager-mobile-app__status${hasMessage ? " is-ready" : ""}`}>
              {hasMessage ? "Ready to Send" : "Draft"}
            </span>
          </div>
          <p className="manager-mobile-app__panel-description">
            Keep the message concise. Shorter notifications are easier to scan on mobile devices.
          </p>
          <div className="manager-mobile-app__editor">
            <CKEditor
              key={editorKey}
              editor={ClassicEditor}
              config={{
                toolbar: {
                  items: [
                    'undo', 'redo',
                    '|',
                    'heading',
                    '|',
                    'bold', 'italic', 'underline', 'strikethrough',
                    '|',
                    'alignment',
                    '|',
                    'bulletedList', 'numberedList', 'outdent', 'indent',
                    '|',
                    'insertImage',
                    '|',
                    'link', 'insertTable', 'mediaEmbed', 'code',
                    '|',
                    'specialCharacters'
                  ]
                },
                plugins: [
                  Essentials, List, Alignment, Paragraph, Bold, Italic, Underline, Link, Mention, Heading, Undo, Code, Strikethrough, Subscript, Superscript, SourceEditing, Code, SpecialCharacters, SpecialCharactersEssentials, SpecialCharactersEmoji, Image, ImageInsert, Indent, IndentBlock, Table, TableToolbar, MediaEmbed
                ],
                initialData: '',
              }}
              onChange={(event, editor) => {
                const editorData = editor.getData();
                const plainText = editorData
                  .replace(/<[^>]*>/g, ' ')
                  .replace(/&nbsp;/g, ' ')
                  .replace(/\s+/g, ' ')
                  .trim();

                setValue(editorData ? `<body>${editorData}</body>` : "");
                setPlainTextValue(plainText);
              }}
              onReady={(editor) => {
                editor.editing.view.change((writer) => {
                  writer.setStyle(
                    "height",
                    "250px",
                    editor.editing.view.document.getRoot()
                  );
                });
              }}
            />
          </div>
          <div className="manager-mobile-app__footer">
            <div className="manager-mobile-app__helper">
              <span>{plainTextValue ? `${plainTextValue.length} characters` : "No message yet"}</span>
              <span>{selectedProgramCount > 0 ? `${selectedProgramCount} programs targeted` : "Select at least one program"}</span>
            </div>
            <button
              type="button"
              className="manager-home__send-btn manager-mobile-app__submit"
              onClick={onSubmit}
              disabled={isSubmitDisabled}
            >
              {isSubmitting ? "Sending..." : "Send Notification"}
            </button>
          </div>
        </article>
      </div>
    </div>
  );
}

export default connect((state) => ({
  program: state.program
}))(PushNotifications);
